import { io, type Socket } from "socket.io-client";

// Wrapper leggero della connessione Socket.IO, fornito come `$socket` (si usa tramite `useSocket`).
// Tiene un registro dei listener per evitare doppioni e per poterli contare nei test.
type Callback = (...args: any[]) => void;

const state = reactive({ connected: false });
let socketInstance: Socket | null = null;
const listenerRegistry = new Map<string, Set<Callback>>(); // evento → callback registrate

function initSocket(url: string): Socket {
  if (socketInstance) return socketInstance;
  const istanza = io(url, {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
  });
  socketInstance = istanza;
  istanza.on("connect", () => {
    state.connected = true;
    // Contesto utente di base, se disponibile
    try {
      const env = useRuntimeConfig();
      const BASE_PROGETTO = env.public.base_progetto;
      const { $localStorage } = useNuxtApp();
      istanza.emit("userConnected", {
        fingerprint: $localStorage?.getItem?.("fingerprint"),
        token: useCookie(`token${BASE_PROGETTO}`).value,
      });
    } catch {
      // fuori dal contesto Nuxt (riconnessione tardiva): la registrazione utente si rifà al prossimo connect
    }
  });
  istanza.on("disconnect", () => (state.connected = false));
  return istanza;
}

function ensureConnected(url: string): void {
  initSocket(url);
}
function get(): Socket | null {
  return socketInstance;
}
function emit(event: string, data?: unknown): void {
  socketInstance?.emit(event, data);
}
function on(event: string, cb: Callback): void {
  if (!socketInstance) return;
  if (!listenerRegistry.has(event)) listenerRegistry.set(event, new Set());
  const set = listenerRegistry.get(event)!;
  if (set.has(cb)) return; // dedupe
  socketInstance.off(event, cb);
  socketInstance.on(event, cb);
  set.add(cb);
}
function off(event: string, cb: Callback): void {
  if (!socketInstance) return;
  socketInstance.off(event, cb);
  const set = listenerRegistry.get(event);
  if (set?.has(cb)) {
    set.delete(cb);
    if (!set.size) listenerRegistry.delete(event);
  }
}
function listenerCount(event: string): number {
  return listenerRegistry.get(event)?.size || 0;
}
function close(): void {
  if (!socketInstance) return;
  socketInstance.close();
  socketInstance = null;
  state.connected = false;
  listenerRegistry.clear();
}

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const url =
    (config.public?.socket_url as string | undefined) ||
    `${location.protocol}//${location.hostname}:${config.public?.socket_port || 3001}`;
  ensureConnected(url);
  return { provide: { socket: { state, ensureConnected, get, emit, on, off, listenerCount, close } } };
});
